<template>
  <avs-card class="pb-4">
    <template v-slot:title>
      <v-toolbar
        class="mt-4 custom-toolbar"
        elevation="0"
      >
        <v-text-field
          clearable
          hide-details
          prepend-inner-icon="mdi-magnify"
          :label="$t('commons.search')"
          v-model="search"
        />
        <v-select
          hide-details
          dense
          outlined
          v-model="statusFilter"
          :items="statusFilterItems"
          :label="$t('commons.status.name')"
          prepend-inner-icon="mdi-filter-variant"
          class="ml-4"
          style="max-width: 240px"
        />
        <v-spacer />
        <v-btn
          color="primary"
          outlined
          class="ml-4"
          :loading="closing"
          @click="closeDialogIsOpen = true"
        >
          <v-icon left>mdi-lock-clock</v-icon>
          {{ $t("Fermer les anciens événements") }}
        </v-btn>
      </v-toolbar>
    </template>
    <template v-slot:content>
      <v-data-table
        class="ma-10"
        :no-data-text="$t('messages.information.no_data')"
        :headers="headers"
        :items="filteredReservations"
        :search="search"
        item-text="text"
        :loading-text="$t('categories.reservation.loading')"
        :no-results-text="$tc('categories.reservation.name', 0)"
        :loading="loading"
        disable-pagination
        hide-default-footer
        @dblclick:row="($event, { item }) => handleInspectionEdit(item)"
      >
        <template v-slot:item.start_date="{ item }">
          {{ toDatetime(item.start_date) }}
        </template>
        <template v-slot:item.end_date="{ item }">
          {{ toDatetime(item.end_date) }}
        </template>
        <template v-slot:item.status="{ item }">
          <component :is="handleStatusComponent(item)" />
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-end">
            <avs-editButton
              @click.stop="handleInspectionEdit(item)"
              :spantext="
                $t('actions.edit', { entity: $tc('categories.reservation.name', 1).toLowerCase() })
              "
            />
            <avs-deleteButton
              @click.stop="handleOpenDeleteDialog(item)"
              :spantext="
                $t('actions.delete', {
                  entity: $tc('categories.reservation.name', 1).toLowerCase()
                })
              "
            />
          </div>
        </template>
      </v-data-table>
      <avs-deleteDialog
        v-model="deleteDialogIsOpen"
        @cancel="handleCancel"
        @delete="handleDelete"
        resource="reservation"
      />
      <v-dialog
        v-model="closeDialogIsOpen"
        max-width="480"
      >
        <v-card>
          <v-card-title class="text-h6">
            {{ $t("Fermer les anciens événements") }}
          </v-card-title>
          <v-card-text>
            {{
              $t(
                "Toutes les réservations dont la date de fin est dépassée seront passées au statut « Fermée ». Continuer ?"
              )
            }}
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              @click="closeDialogIsOpen = false"
              :disabled="closing"
            >
              {{ $t("commons.cancel") }}
            </v-btn>
            <v-btn
              color="primary"
              :loading="closing"
              @click="handleCloseExpired"
            >
              {{ $t("Confirmer") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </avs-card>
</template>

<script>
import { Closed, Ongoing, Overdue, Pending, Cancelled } from "@/components/generics/status"
import { ReservationService, ProjectService } from "@/services"
import { toDatetime } from "@/utils/datetime"
import { RESERVATION_STATUS_ITEMS } from "@/utils/consts"

export default {
  name: "AllReservations",
  components: { Closed, Ongoing, Overdue, Pending, Cancelled },
  data() {
    return {
      deleteDialogIsOpen: false,
      headers: [
        { text: this.$t("Événement"), value: "event.name", class: "min-width-200" },
        { text: this.$t("Espace"), value: "space.name", class: "min-width-200" },
        { text: this.$t("Client"), value: "customer.full_name", class: "min-width-200" },
        { text: this.$t("Date de début"), value: "start_date", class: "min-width-150" },
        { text: this.$t("Date de fin"), value: "end_date", class: "min-width-150" },
        { text: this.$t("commons.status.name"), align: "center", value: "status" },
        {
          text: this.$t("commons.actions"),
          sortable: false,
          value: "actions",
          align: "right",
          width: 150
        }
      ],
      reservations: [],
      loading: false,
      closing: false,
      closeDialogIsOpen: false,
      search: "",
      selectedItem: null,
      statusFilter: "Ongoing"
    }
  },
  created() {
    this.getAllReservations()
  },
  computed: {
    statusFilterItems() {
      return [
        { text: this.$t("commons.all_simple"), value: null },
        ...RESERVATION_STATUS_ITEMS.map(s => ({ text: s.text, value: s.value }))
      ]
    },
    filteredReservations() {
      if (!this.statusFilter) return this.reservations
      return this.reservations.filter(r => r.status === this.statusFilter)
    }
  },
  methods: {
    getAllReservations() {
      this.loading = true
      ReservationService.getAllReservations().then(response => {
        this.reservations = response
        this.loading = false
      })
    },
    async handleCloseExpired() {
      this.closing = true
      try {
        const { closed } = await ReservationService.closeExpired()
        this.notify(this.$t("{count} réservation(s) fermée(s)", { count: closed }), "success")
        this.getAllReservations()
      } catch (e) {
        this.notify(this.$t("messages.error.smtg_wrong"), "error")
      } finally {
        this.closing = false
        this.closeDialogIsOpen = false
      }
    },
    handleStatusComponent(item) {
      return item.status
    },
    async setStoreFromProjectId(projectId) {
      return ProjectService.get(this, null, projectId).then(project => {
        this.$store.commit("app/setCompany", project.company)
        this.$store.commit("app/setProject", project)
      })
    },
    async handleInspectionEdit(item) {
      this.setStoreFromProjectId(item.event.id).then(() => {
        this.$router.push({ name: "Reservations", params: { id: item.id } })
      })
    },
    handleOpenDeleteDialog(item) {
      this.deleteDialogIsOpen = true
      this.selectedItem = item
    },
    handleCancel() {
      this.deleteDialogIsOpen = false
    },
    handleDelete(item) {
      ReservationService.remove(this, this.selectedItem.id).then(r => {
        this.reservations = this.reservations.filter(nc => nc.id !== this.selectedItem.id)
        let message = this.$t("messages.success.deleted", {
          entity: this.$tc("reservation")
        })
        this.notify(message, "success")
      })
      this.deleteDialogIsOpen = false
    },
    notify(message, color) {
      this.$store.commit("app/message", { msg: message, color: color })
    },
    isInspectionOver(item) {
      return ["Closed", "Overdue"].includes(item.status)
    },
    toDatetime
  }
}
</script>
